import React from 'react';
import { Instagram, Linkedin, Send, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import turetLogo from '../assets/turet-logo.jpg';

const Footer = () => {
    const socialLinks = [
        { icon: Instagram, href: "https://www.instagram.com/mskuturet?igsh=MTN5aG9taXRzYjEwZA==" },
        { icon: Linkedin, href: "https://www.linkedin.com/in/msk%C3%BC-turist-rehberli%C4%9Fi-toplulu%C4%9Fu-809140357/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" }
    ];

    const quickLinks = [
        { name: 'Ana Sayfa', path: '/' },
        { name: 'Vizyonumuz', path: '/vizyon' },
        { name: 'Misyonumuz', path: '/misyon' },
        { name: 'Etkinlikler', path: '/etkinlikler' },
        { name: 'İletişim', path: '/iletisim' }
    ];

    return (
        <footer id="iletisim" className="relative -mt-20 z-10">

            {/* 1. LAYER: Glow Effects Behind Footer */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-full bg-cyan-500/20 blur-[120px] rounded-full opacity-40 pointer-events-none" />

            {/* 2. LAYER: Main Footer Container with Curved Top */}
            <div className="relative bg-[#020617] rounded-t-[5rem] overflow-hidden">

                {/* Decorative Top Border Line */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

                <div className="pt-10 pb-6 px-6 relative z-10">
                    <div className="max-w-7xl mx-auto">

                        {/* Centered Grid Layout */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-20 text-center relative">

                            {/* Vertical Divider Lines (Mockup for large screens) */}
                            <div className="hidden md:block absolute top-10 bottom-10 left-1/3 w-[1px] bg-gradient-to-b from-transparent via-white/5 to-transparent" />
                            <div className="hidden md:block absolute top-10 bottom-10 right-1/3 w-[1px] bg-gradient-to-b from-transparent via-white/5 to-transparent" />

                            {/* Brand Column */}
                            <div className="flex flex-col items-center group">
                                <div className="relative mb-6">
                                    <div className="absolute inset-0 bg-cyan-500 blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 rounded-full" />
                                    <div className="relative w-24 h-24 rounded-full p-1 bg-gradient-to-tr from-cyan-500/20 to-purple-500/20 border border-white/10">
                                        <div className="w-full h-full rounded-full overflow-hidden">
                                            <img src={turetLogo} alt="MSKÜ TURET" className="w-full h-full object-cover" />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 mb-2 font-heading max-w-xs text-center leading-tight">
                                    Muğla Sıtkı Koçman Üniversitesi
                                </h3>
                                <p className="text-cyan-400/80 text-xs font-bold tracking-[0.1em] uppercase">
                                    Turist Rehberliği Topluluğu
                                </p>
                            </div>

                            {/* Quick Links Column */}
                            <div className="flex flex-col items-center justify-center">
                                <h4 className="text-white font-bold mb-8 flex items-center gap-3">
                                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                                    Hızlı Bağlantılar
                                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
                                </h4>
                                <ul className="space-y-4">
                                    {quickLinks.map((item) => (
                                        <li key={item.name}>
                                            <Link
                                                to={item.path}
                                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                                className="group flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-all duration-300"
                                            >
                                                <span className="w-0 h-[1px] bg-cyan-400 transition-all duration-300 group-hover:w-4 opacity-0 group-hover:opacity-100" />
                                                <span className="relative">
                                                    {item.name}
                                                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-cyan-400 transition-all duration-300 group-hover:w-full" />
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Contact / Social Column */}
                            <div className="flex flex-col items-center justify-center">
                                <h4 className="text-white font-bold mb-8 flex items-center gap-3">
                                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                                    İletişim & Sosyal
                                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                                </h4>

                                <a
                                    href="mailto:mskuturistrehberligihesabi@gmail.com"
                                    className="relative px-8 py-3 rounded-full bg-gradient-to-r from-cyan-900/20 to-purple-900/20 border border-white/10 overflow-hidden group mb-8 transition-all hover:border-cyan-500/50"
                                >
                                    <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="flex items-center gap-3 relative z-10">
                                        <Send className="w-4 h-4 text-cyan-400" />
                                        <span className="text-gray-300 group-hover:text-white text-sm font-medium">Bize Mesaj Gönder</span>
                                    </div>
                                </a>

                                <div className="flex gap-5">
                                    {socialLinks.map((social, i) => (
                                        <a key={i} href={social.href} target="_blank" rel="noopener noreferrer" className="group relative w-10 h-10">
                                            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500 to-purple-600 rounded-xl blur opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
                                            <div className="relative w-full h-full bg-[#0f172a] border border-white/10 rounded-xl flex items-center justify-center text-gray-400 group-hover:text-white transition-colors">
                                                <social.icon className="w-5 h-5" />
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* Bottom Bar */}
                        <div className="pt-8 border-t border-white/5 flex flex-col items-center gap-6">
                            <p className="text-xs text-gray-500 font-medium">
                                &copy; 2026 MSKÜ TURET &bull; <span className="text-gray-600">Tüm hakları saklıdır.</span>
                            </p>

                            <a
                                href="https://www.instagram.com/rabiacengizhan?igsh=MXRrNGxoaTkwMWF6dg=="
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] hover:bg-white/[0.08] transition-colors group cursor-pointer border border-transparent hover:border-white/5"
                            >
                                <Instagram className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
                                <span className="text-[10px] text-gray-500 group-hover:text-gray-300 uppercase tracking-widest font-semibold">
                                    DESIGN BY RABİA CENGİZHAN
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
